/**
 * createGa4Transport — GA4 transport adapter for `createMarketingAnalytics` (AISEO-GA4-001).
 *
 * Sibling of `createHttpAnalyticsTransport`: same `{track(envelope)}` contract, same
 * never-throw-from-track guarantee, different destination. Passing this as the transport routes
 * the existing marketing analytics pipeline — UTM first/last-touch attribution, session ids,
 * locale, consent gating, pre-consent buffering — into GA4 without duplicating any of it.
 *
 * **This is deliberately a transport, not a parallel analytics system.** The alternative (calling
 * `window.gtag` directly from components) would bypass the consent gate and the attribution
 * enrichment that `createMarketingAnalytics` already owns, and it is what the
 * `web/no-bare-analytics-track` ESLint rule exists to forbid.
 *
 * ## Two GA4 constraints that shape this file
 *
 * 1. **Event names are `[A-Za-z0-9_]`, ≤40 chars, and must not start with a digit.** The
 *    marketing pipeline emits dotted namespaced names (`marketing.parcel.cta_clicked`), which GA4
 *    silently drops. So names are normalised here rather than at the call sites — one rule, one
 *    place, and the original is preserved in `event_name_raw` so nothing is lost.
 * 2. **Reserved parameter names are rejected by GA4.** `page_title`/`page_location` are meaningful
 *    to GA and passed through; anything colliding with a GA-reserved prefix (`ga_`, `google_`,
 *    `firebase_`) is dropped, because sending it poisons the whole event rather than that field.
 *
 * @module @puffin-core/marketing-shell/analytics/createGa4Transport
 */

import requireOnError from './requireOnError.js';

/** GA4 hard limits, from the Measurement Protocol / gtag reference. */
const MAX_EVENT_NAME_LENGTH = 40;
const MAX_PARAM_NAME_LENGTH = 40;
const MAX_PARAM_VALUE_LENGTH = 100;

/** Prefixes GA4 reserves; a param using one invalidates the event. */
const RESERVED_PARAM_PREFIXES = Object.freeze(['ga_', 'google_', 'firebase_']);

/**
 * Normalise a namespaced marketing event name into a GA4-legal one.
 *
 * `marketing.parcel.cta_clicked` → `marketing_parcel_cta_clicked`
 *
 * Truncation keeps the TAIL, not the head: every event here shares the `marketing_<appKey>_`
 * prefix, so trimming from the front would collapse distinct events into identical truncated
 * names. The distinguishing part is always at the end.
 *
 * @param {string} name
 * @returns {string}
 */
export function toGa4EventName(name) {
  const cleaned = String(name)
    .replace(/[^A-Za-z0-9_]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '');
  const safe = /^[0-9]/.test(cleaned) ? `e_${cleaned}` : cleaned;
  return safe.length <= MAX_EVENT_NAME_LENGTH
    ? safe
    : safe.slice(safe.length - MAX_EVENT_NAME_LENGTH).replace(/^_+/, '');
}

/**
 * Coerce one property into a GA4-safe parameter value.
 *
 * Objects are JSON-stringified rather than dropped — a dropped field is invisible in GA and
 * indistinguishable from "never sent", whereas a truncated string is at least diagnosable.
 *
 * @param {unknown} value
 * @returns {string|number|boolean|undefined}
 */
function toGa4Value(value) {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  const asString = typeof value === 'string' ? value : JSON.stringify(value);
  if (typeof asString !== 'string') return undefined;
  return asString.length > MAX_PARAM_VALUE_LENGTH
    ? asString.slice(0, MAX_PARAM_VALUE_LENGTH)
    : asString;
}

/**
 * Map an envelope's properties onto a GA4 params object.
 *
 * @param {Object} properties
 * @returns {Object}
 */
export function toGa4Params(properties) {
  const out = {};
  if (!properties || typeof properties !== 'object') return out;
  for (const [rawKey, rawValue] of Object.entries(properties)) {
    const key = String(rawKey)
      .replace(/[^A-Za-z0-9_]/g, '_')
      .slice(0, MAX_PARAM_NAME_LENGTH);
    if (!key) continue;
    if (RESERVED_PARAM_PREFIXES.some((p) => key.toLowerCase().startsWith(p))) continue;
    const value = toGa4Value(rawValue);
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/**
 * @typedef {Object} Ga4TransportConfig
 * @property {Object} [windowRef]            Injectable window (tests / SSR).
 * @property {(error: Error) => void} onError  REQUIRED: receives a throwing tag's failure, which
 *   is reported, never swallowed.
 * @property {string} [measurementId]        When set, events are sent with `send_to`, which is
 *   required if the page hosts more than one GA4 property.
 */

/**
 * Build the GA4 transport.
 *
 * @param {Ga4TransportConfig} config
 * @returns {{track: (envelope: Object) => void}}
 * @throws {TypeError} when `config` is not an object, or `onError` is not a function
 */
export default function createGa4Transport(config) {
  if (typeof config !== 'object' || config === null) {
    throw new TypeError('createGa4Transport: config must be an object');
  }
  const { windowRef, onError, measurementId } = config;
  requireOnError('createGa4Transport', onError);

  return {
    track(envelope) {
      try {
        const win = windowRef || (typeof window !== 'undefined' ? window : null);
        // No gtag means the tag has not loaded (SSR, blocked by an ad-blocker, or no measurement
        // ID configured yet). That is an expected steady state during rollout, not an error —
        // dropping silently is correct, and `createMarketingAnalytics` has already decided this
        // event was consented.
        if (!win || typeof win.gtag !== 'function') return;
        if (!envelope || typeof envelope !== 'object' || !envelope.name) return;

        const params = toGa4Params(envelope.properties);
        const normalised = toGa4EventName(envelope.name);
        // `event_name_raw` and `send_to` below are GA4 WIRE-FORMAT parameter names defined by
        // Google. Renaming them to camelCase would send parameters GA4 does not recognise, so the
        // snake_case is the external contract rather than our field naming.
        if (normalised !== envelope.name) {
          // eslint-disable-next-line web/no-field-mapping -- Category M: GA4 wire-format name.
          params.event_name_raw = toGa4Value(envelope.name);
        }
        // eslint-disable-next-line web/no-field-mapping -- Category M: GA4 wire-format name (see above).
        if (measurementId) params.send_to = measurementId;

        win.gtag('event', normalised, params);
      } catch (err) {
        // track() must never throw into a component render path.
        onError(err instanceof Error ? err : new Error(String(err)));
      }
    },
  };
}
